/**
 * Columns the user can show or hide. The pinned Backtest identity column (name, portfolio, and
 * strategies) and the row actions are always on, so they are not listed here.
 */
export type PortfolioBacktestColumnId =
    | 'id'
    | 'capital'
    | 'status'
    | 'numTrades'
    | 'startingBalance'
    | 'adjustedEndingBalance'
    | 'replenishmentCount'
    | 'replenishmentAmount'
    | 'percentageChange'
    | 'cagr'
    | 'returnOnRiskCapital'
    | 'sharpeRatio'
    | 'maxDrawdown'
    | 'spyReturnPercent'
    | 'universeBuyHoldReturnPercent'
    | 'timingEdgePercent'
    | 'walkForward'
    | 'startDateTime'
    | 'endDateTime';

export interface PortfolioBacktestColumnConfig {
    id: PortfolioBacktestColumnId;
    label: string;
    defaultVisible: boolean;
}

export const PORTFOLIO_BACKTEST_VISIBLE_COLUMNS_STORAGE_KEY = 'portfolioBacktest.visibleColumns.v3';
const PRE_CAGR_VISIBLE_COLUMNS_STORAGE_KEY = 'portfolioBacktest.visibleColumns.v2';

/**
 * v1 predates the Trades column: the trade count lived in the always-visible actions column. A v1
 * selection is carried forward with the columns added since, so upgrading never hides the count.
 */
const LEGACY_VISIBLE_COLUMNS_STORAGE_KEY = 'portfolioBacktest.visibleColumns.v1';
const COLUMNS_ADDED_SINCE_LEGACY_STORAGE: readonly PortfolioBacktestColumnId[] = ['numTrades', 'cagr'];

/**
 * Shared disclaimer for the portfolio Timing Edge % stat (random-entry
 * baselines design §6, gross-vs-net + derivative exclusion).
 */
export const TIMING_EDGE_DISCLAIMER =
    'Random-entry CONTROL: exposure-matched mean per-trade edge vs random same-name/month/horizon entries. ' +
    'Stock and immutably attributable single-leg option trades are supported; mixed, multi-leg, and futures trades are excluded. ' +
    'Dynamic exits use the nearest fixed-horizon proxy and overlong holds are excluded. Stock returns are net of simulated ' +
    'costs while the stock control is cost-free; option controls use the experiment option fee convention. Final zero-sample ' +
    'buckets are excluded as explicitly non-comparable; blank means no sampled cohort exists or coverage is incomplete or pending.';

type TimingEdgeCoverage = {
    timingEdgePercent?: number | null;
    timingEdgeEligibleTradeCount?: number | null;
    timingEdgeCoveredTradeCount?: number | null;
};

/** Compact persisted coverage label; a published shortfall is final-zero-sample by contract. */
export const getTimingEdgeCoverageLabel = (coverage: TimingEdgeCoverage): string | null => {
    const eligibleTradeCount = coverage.timingEdgeEligibleTradeCount;
    const coveredTradeCount = coverage.timingEdgeCoveredTradeCount;
    if (eligibleTradeCount == null || coveredTradeCount == null) return null;

    const baseLabel = `${coveredTradeCount}/${eligibleTradeCount}`;
    if (coverage.timingEdgePercent == null) return baseLabel;

    const finalZeroSampleTradeCount = Math.max(eligibleTradeCount - coveredTradeCount, 0);
    return finalZeroSampleTradeCount > 0 ? `${baseLabel}; ${finalZeroSampleTradeCount} no sample` : baseLabel;
};

/** Row-specific timing-edge coverage explanation for list and detail views. */
export const getTimingEdgeTooltip = (coverage: TimingEdgeCoverage): string => {
    const eligibleTradeCount = coverage.timingEdgeEligibleTradeCount;
    const coveredTradeCount = coverage.timingEdgeCoveredTradeCount;

    if (eligibleTradeCount == null || coveredTradeCount == null) return TIMING_EDGE_DISCLAIMER;

    if (coverage.timingEdgePercent == null) {
        return (
            `${TIMING_EDGE_DISCLAIMER} Coverage: ${coveredTradeCount}/${eligibleTradeCount} eligible trades have sampled controls; ` +
            'at least one remaining comparison is missing, non-final, or otherwise unavailable.'
        );
    }

    const finalZeroSampleTradeCount = Math.max(eligibleTradeCount - coveredTradeCount, 0);
    if (finalZeroSampleTradeCount === 0) {
        return `${TIMING_EDGE_DISCLAIMER} Coverage: ${coveredTradeCount}/${eligibleTradeCount} eligible trades have sampled controls.`;
    }

    return (
        `${TIMING_EDGE_DISCLAIMER} Coverage: ${coveredTradeCount}/${eligibleTradeCount} eligible trades have sampled controls; ` +
        `${finalZeroSampleTradeCount} final zero-sample ${finalZeroSampleTradeCount === 1 ? 'trade was' : 'trades were'} excluded as non-comparable.`
    );
};

/**
 * The persisted evaluation uses the saved reporting split. Its numerical criteria are not a test
 * of whether component selection left that data untouched; the cell qualifies known window overlap.
 */
export const WALK_FORWARD_DISCLAIMER =
    'Historical evaluation after the saved reporting split: the strategy signal stream must stay positive over at least ' +
    '100 trades, so a profitable portfolio can still have an inconclusive criteria result on a thin sample. ' +
    'The percentage shown is the portfolio’s average return per trade, not an account return. Meeting these criteria ' +
    'does not establish an untouched holdout; component selection may have used this data, and some selection windows ' +
    'are unrecorded. Blank = no saved evaluation.';

export const portfolioBacktestColumnRegistry: PortfolioBacktestColumnConfig[] = [
    { id: 'id', label: 'ID', defaultVisible: true },
    // Off by default: the same signature is in every expanded row, so it only earns a column when comparing runs.
    { id: 'capital', label: 'Capital', defaultVisible: false },
    { id: 'status', label: 'Status', defaultVisible: true },
    { id: 'numTrades', label: 'Trades', defaultVisible: true },
    // Off by default: a portfolio's runs almost always share one starting balance, so the column
    // costs width without separating rows. The uniform-column hint surfaces it when that changes.
    { id: 'startingBalance', label: 'Start Balance', defaultVisible: false },
    { id: 'adjustedEndingBalance', label: 'End Balance', defaultVisible: true },
    { id: 'replenishmentCount', label: 'Replenishment Count', defaultVisible: true },
    { id: 'replenishmentAmount', label: 'Replenishment Amount', defaultVisible: true },
    { id: 'percentageChange', label: '% Change', defaultVisible: true },
    { id: 'cagr', label: 'CAGR', defaultVisible: true },
    { id: 'returnOnRiskCapital', label: 'ARORC', defaultVisible: true },
    { id: 'sharpeRatio', label: 'Sharpe', defaultVisible: true },
    { id: 'maxDrawdown', label: 'Max Drawdown', defaultVisible: true },
    { id: 'spyReturnPercent', label: 'SPY %', defaultVisible: true },
    { id: 'universeBuyHoldReturnPercent', label: 'Universe B&H %', defaultVisible: true },
    { id: 'timingEdgePercent', label: 'Timing Edge %', defaultVisible: true },
    { id: 'walkForward', label: 'Evaluation', defaultVisible: true },
    // Off by default for the same reason: runs in a portfolio typically share one research window start.
    { id: 'startDateTime', label: 'Start Date', defaultVisible: false },
    { id: 'endDateTime', label: 'End Date', defaultVisible: true }
];

export const portfolioBacktestColumnPresets = {
    // The headline results only, for narrow screens.
    compact: new Set<PortfolioBacktestColumnId>([
        'id',
        'status',
        'percentageChange',
        'cagr',
        'returnOnRiskCapital',
        'sharpeRatio',
        'maxDrawdown',
        'timingEdgePercent',
        'walkForward'
    ]),
    performance: new Set<PortfolioBacktestColumnId>([
        'id',
        'status',
        'numTrades',
        'startingBalance',
        'adjustedEndingBalance',
        'percentageChange',
        'cagr',
        'returnOnRiskCapital',
        'sharpeRatio',
        'maxDrawdown',
        'timingEdgePercent',
        'walkForward',
        'startDateTime',
        'endDateTime'
    ]),
    full: new Set<PortfolioBacktestColumnId>(portfolioBacktestColumnRegistry.map((column) => column.id))
} as const;

export const getDefaultPortfolioBacktestColumns = (): Set<PortfolioBacktestColumnId> => {
    return new Set(portfolioBacktestColumnRegistry.filter((column) => column.defaultVisible).map((column) => column.id));
};

export const isPortfolioBacktestColumnId = (value: string): value is PortfolioBacktestColumnId => {
    return portfolioBacktestColumnRegistry.some((column) => column.id === value);
};

/** Known column ids from a stored selection, or null when it is missing, malformed, or names no current column. */
const parseStoredColumnIds = (storedValue: string | null): PortfolioBacktestColumnId[] | null => {
    if (!storedValue) return null;

    let parsedValue: unknown;

    try {
        parsedValue = JSON.parse(storedValue);
    } catch {
        return null;
    }

    if (!Array.isArray(parsedValue)) return null;

    // Ids of retired columns (v1's Portfolio, Name, and Strategies) are dropped here.
    const columnIds = parsedValue.filter(
        (columnId): columnId is PortfolioBacktestColumnId => typeof columnId === 'string' && isPortfolioBacktestColumnId(columnId)
    );

    return columnIds.length > 0 ? columnIds : null;
};

/** The saved column selection, migrating a v1 selection forward, or the defaults when nothing usable is saved. */
export const loadPortfolioBacktestVisibleColumns = (storage: Pick<Storage, 'getItem'>): Set<PortfolioBacktestColumnId> => {
    const storedColumns = parseStoredColumnIds(storage.getItem(PORTFOLIO_BACKTEST_VISIBLE_COLUMNS_STORAGE_KEY));

    if (storedColumns) return new Set(storedColumns);

    const preCagrColumns = parseStoredColumnIds(storage.getItem(PRE_CAGR_VISIBLE_COLUMNS_STORAGE_KEY));
    if (preCagrColumns) return new Set([...preCagrColumns, 'cagr']);

    const legacyColumns = parseStoredColumnIds(storage.getItem(LEGACY_VISIBLE_COLUMNS_STORAGE_KEY));

    if (legacyColumns) return new Set([...legacyColumns, ...COLUMNS_ADDED_SINCE_LEGACY_STORAGE]);

    return getDefaultPortfolioBacktestColumns();
};
